/**
 * Human-readable labels for the lead feed and the Audit Report. ONE source.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A SHARED FILE AND NOT TWO LOCAL MAPS
 * ---------------------------------------------------------------------------
 * SendHistory carried its own map of eight email types while email_log.email_type
 * actually takes eleven, and the three it had never heard of — document_completed,
 * signature_request, document_batch — fell through its `?? r.email_type` fallback
 * and printed the raw column value on the Audit Report. A second copy of that map
 * was then made for the Email tab, inheriting the same gap on the same day.
 *
 * A fallback that prints the raw value is why nobody noticed: it looks like a
 * label, so it reads as a deliberate name rather than a miss. The fallback here
 * humanises instead — an unknown type renders "Document Completed", which is
 * still wrong-ish but is wrong in a way that does not look like database
 * internals leaking onto a client's file.
 *
 * ADD A NEW TYPE IN THE SAME CHANGE THAT STARTS EMITTING IT. Both maps below are
 * complete as of migration 040; keeping them that way is cheaper than finding
 * the next one on screen.
 */

/** Anything unknown still reads as words: 'stage_change' -> 'Stage Change'. */
export function humanize(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim();
  if (!s) return 'Activity';
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/**
 * Every value email_log.email_type takes. Verified against the emailType
 * constants in backend/src/lib/emailTemplates.ts — all eleven.
 */
export const EMAIL_TYPE_LABEL: Record<string, string> = {
  estimate: 'Estimate',
  invoice: 'Invoice',
  inventory_request: 'Inventory request',
  contractor_estimate: 'Contractor estimate',
  contractor_work_order: 'Contractor work order',
  move_booked: 'Move booked',
  booking_confirmation: 'Booking confirmation',
  payment_receipt: 'Payment receipt',
  new_lead: 'New-lead acknowledgement',
  // The three that used to print raw.
  document_completed: 'Signed document',
  signature_request: 'Signature request',
  document_batch: 'Documents sent',
};

export const emailTypeLabel = (t: string | null | undefined): string =>
  EMAIL_TYPE_LABEL[String(t ?? '')] ?? humanize(t);

/**
 * activity_log.activity_type. The feed printed these raw and uppercased, so a
 * stage change announced itself as "STAGE_CHANGE" on a page reps read all day.
 */
export const ACTIVITY_TYPE_LABEL: Record<string, string> = {
  note: 'Note',
  system: 'Activity',
  stage_change: 'Stage change',
  follow_up_created: 'Follow-up created',
  follow_up_completed: 'Follow-up completed',
};

export const activityTypeLabel = (t: string | null | undefined): string =>
  ACTIVITY_TYPE_LABEL[String(t ?? '')] ?? humanize(t);

/** Delivery state on an email_log row. Bounced can never read as success. */
export const SEND_STATUS: Record<string, { label: string; color: string }> = {
  delivered: { label: 'Delivered', color: 'var(--crm-green)' },
  sent: { label: 'Sent', color: 'var(--crm-amber)' },
  delivery_delayed: { label: 'Delayed', color: 'var(--crm-amber)' },
  blocked: { label: 'Blocked', color: 'var(--crm-amber)' },
  bounced: { label: 'Bounced', color: 'var(--crm-red)' },
  failed: { label: 'Failed', color: 'var(--crm-red)' },
  complained: { label: 'Complained', color: 'var(--crm-red)' },
};

export const sendStatus = (s: string | null | undefined): { label: string; color: string } =>
  SEND_STATUS[String(s ?? '')] ?? { label: humanize(s), color: 'var(--crm-muted)' };
